import { useEffect, useRef } from "react";
import { MapLocation } from "types/map";
import {
  hasLatLong,
  buildItineraryRouteKey,
  getLocationDisplayTitle,
} from "libraries/map";
import { getPlaceTypeMarkerIconUrl } from "libraries/placeTypeIcons";
import { getItineraryMarkerBadge } from "libraries/itineraryMarker";

type Props = {
  map: google.maps.Map | null;
  from: MapLocation;
  destinations: MapLocation[];
};

type MarkerSpec = {
  location: MapLocation;
  role: "start" | "end" | "stop";
  roleLabel: string;
};

const createMarkerContent = (iconUrl: string, badge: string, alt: string) => {
  const content = document.createElement("div");
  content.style.position = "relative";
  content.style.width = "40px";
  content.style.height = "48px";
  content.innerHTML = `
    <img
      style="width: 40px; height: 48px; display: block; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.25));"
      src="${iconUrl}"
      alt="${alt}"
    />
    <span
      style="
        position: absolute;
        top: -2px;
        right: -4px;
        min-width: 18px;
        height: 18px;
        padding: 0 4px;
        border-radius: 9px;
        background: #1976d2;
        color: #fff;
        font: 700 11px/18px system-ui, sans-serif;
        text-align: center;
        box-shadow: 0 1px 2px rgba(0,0,0,0.25);
      "
    >${badge}</span>
  `;
  return content;
};

const MapItineraryMarkers = ({ map, from, destinations }: Props) => {
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const routeKey = buildItineraryRouteKey(from, destinations);

  useEffect(() => {
    if (!map) return;

    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];

    const filledDestinations = destinations
      .map((location, index) => ({ location, index }))
      .filter(({ location }) => hasLatLong(location));

    const specs: MarkerSpec[] = [];

    if (hasLatLong(from)) {
      specs.push({ location: from, role: "start", roleLabel: "Start" });
    }

    filledDestinations.forEach(({ location }, filledIndex) => {
      const isLast = filledIndex === filledDestinations.length - 1;
      specs.push({
        location,
        role: isLast ? "end" : "stop",
        roleLabel: isLast ? "End" : `Stop ${filledIndex + 1}`,
      });
    });

    specs.forEach(({ location, role, roleLabel }) => {
      const title = getLocationDisplayTitle(location);
      const badge = getItineraryMarkerBadge(role, roleLabel);
      const iconUrl = getPlaceTypeMarkerIconUrl(location.placeTypes);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: {
          lat: location.latitude ?? 0,
          lng: location.longitude ?? 0,
        },
        content: createMarkerContent(
          iconUrl,
          badge,
          `${roleLabel}: ${title}`
        ),
        title: `${roleLabel}: ${title}`,
      });

      markersRef.current.push(marker);
    });

    return () => {
      markersRef.current.forEach((marker) => {
        marker.map = null;
      });
      markersRef.current = [];
    };
  }, [map, routeKey]);

  return null;
};

export default MapItineraryMarkers;
